import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'

/**
 * Die Breite der Spalten, von Hand gezogen (7.3 Abschnitt 10).
 *
 * **Zwei Gruppen, nicht zwanzig Spalten.** Die Positionsspalte hat ihre eigene
 * Breite — sie trägt Text, alle anderen tragen Zahlen. Und alle Zahlenspalten
 * teilen sich EINE Breite: Wer im Konzernaufriss an einer Gesellschaftsspalte
 * zieht, hat damit alle gezogen. Zwanzig Gesellschaften einzeln zu fassen wäre
 * zwanzigmal dieselbe Entscheidung, und keine davon interessiert den Anwender.
 *
 * Der Preis ist benannt: Die Konzernspalte trägt die größte Zahl und bekommt
 * doch dieselbe Breite wie die kleinste Gesellschaft. Wo es klemmt, zieht man
 * einmal auf, und es klemmt nirgends mehr.
 */
export type Spaltenbreiten = {
  /** Die erste Spalte — Position, Konto, Zeilenname. */
  position: number
  /** Jede andere Spalte der Tabelle. */
  wert: number
}

/** Unter diese Breite geht es nicht: Darunter ist eine Spalte kein Inhalt mehr, sondern ein Strich. */
const MINDEST_POSITION = 120
const MINDEST_WERT = 48

/**
 * Wie schmal die **Vorgabe** die Positionsspalte machen darf (R487).
 *
 * Nicht `MINDEST_POSITION`: Die 120 Pixel gehören dem Zug — wer eine Spalte
 * selbst zusammenschiebt, weiß, was er tut. Die Vorgabe darf ihm keine Spalte
 * hinstellen, die er nie so gewollt hätte; 330 Pixel sind dieselbe Breite, die
 * die Positionsspalte ungezogen ohnehin hat (`stil.css`, R418).
 */
const VORGABE_POSITION = 330

/**
 * Wie nah an der Spaltenkante der Griff greift — **beidseits** (R448).
 *
 * Die Linie ist die Marke, und die Fläche liegt um sie herum statt neben ihr:
 * Wer eine Kante anfährt, zielt auf den Strich. Zwei Griffe können sich dabei
 * nicht berühren — eine Zahlenspalte ist mindestens `MINDEST_WERT` breit, und
 * dazwischen bleiben 38 Pixel, in denen keiner greift.
 */
const GRIFF = 5

const ABLAGE = 'coko.spaltenbreiten.'

/**
 * Die Breite lebt im Browser dessen, der sie gezogen hat.
 *
 * **Anders als Aufriss und Schichten steht sie NICHT in der Adresse** (R156):
 * Die sagen etwas über die Zahlen aus und gehören deshalb in einen
 * weitergegebenen Verweis. Eine Spaltenbreite sagt etwas über den Bildschirm
 * aus, vor dem einer sitzt — sie mitzuschicken hieße, dem Empfänger die
 * eigene Fensterbreite aufzudrängen.
 */
function gelesen(kennung: string): Spaltenbreiten | null {
  try {
    const roh = window.localStorage.getItem(ABLAGE + kennung)
    if (!roh) {
      return null
    }

    const b = JSON.parse(roh) as Partial<Spaltenbreiten>
    return typeof b?.position === 'number' && typeof b?.wert === 'number'
      ? { position: b.position, wert: b.wert }
      : null
  } catch {
    // Ein Browser ohne Speicher (privates Fenster, gesperrte Seitendaten) ist
    // kein Fehlerfall: Dann gilt die gerechnete Breite, wie vor dieser Auflage.
    return null
  }
}

function geschrieben(kennung: string, breiten: Spaltenbreiten | null): void {
  try {
    if (breiten) {
      window.localStorage.setItem(ABLAGE + kennung, JSON.stringify(breiten))
    } else {
      window.localStorage.removeItem(ABLAGE + kennung)
    }
  } catch {
    /* siehe oben */
  }
}

/**
 * **Der Rahmen, der sich meldet, wenn es ihn gibt** (Befund vom 2026-09-29).
 *
 * Bis hierher lag der Rahmen in einem `useRef`, und der Zug hing in einem
 * Effekt, der genau einmal lief. Das ging gut, solange die Tabelle vom ersten
 * Bildaufbau an dastand. In der IC-Abstimmung steht sie das nicht: Erst kommt
 * „wird geladen", dann die Tabelle — und zu der Zeit, als der Effekt lief, war
 * der Rahmen noch gar nicht da. Der Zug hing an nichts, und keine Spalte ließ
 * sich ziehen (im Browser nachgemessen: die Kopfzelle bekam beim Überfahren
 * kein `amgriff`).
 *
 * Dasselbe trifft jede Tabelle, die zwischendurch verschwindet — ein
 * Blattwechsel, ein neuer Bericht, ein Ladevorgang. Deshalb wird der Rahmen
 * nicht abgelegt, sondern **gemeldet**: Er steht im Zustand, und wer an ihm
 * hängt, hängt neu an, sobald er wechselt.
 */
function useFeld<T extends HTMLElement>(): [HTMLElement | null, (el: T | null) => void] {
  const [feld, setFeld] = useState<HTMLElement | null>(null)
  const melden = useCallback((el: T | null) => setFeld(el), [])
  return [feld, melden]
}

/**
 * Die Breiten einer Tabelle — gelesen, gezogen, gemerkt.
 *
 * `kennung` benennt das Bild, nicht die Tabelle: „bilanz:struktur" und
 * „bilanz:konsolidierung" haben verschiedene Spalten und verdienen deshalb
 * verschiedene Breiten. Wer das Bild wechselt, findet seine eigene Einstellung
 * vor und nicht die des Nachbarbildes.
 */
export function useSpaltenbreiten<T extends HTMLElement = HTMLDivElement>(
  kennung: string,
  /**
   * Wie viele schmale Spalten VOR der Positionsspalte stehen — die
   * Nummernspalte der Kapitalflussrechnung ist die einzige. Der Zug muss sie
   * kennen: Ohne diese Angabe schrieb er die Breite der Positionsspalte an
   * die erste Spalte der Leiste, und das war während des Zugs die Nummer.
   */
  vorspalten = 0,
) {
  const [gezogen, setGezogen] = useState<Spaltenbreiten | null>(() => gelesen(kennung))

  /**
   * Die gemessene Vorgabe — **alle Zahlenspalten gleich breit** (R487).
   *
   * `null` heißt „noch nicht gemessen": Dann steht keine Spaltenleiste da, der
   * Browser rechnet die natürlichen Breiten, und genau die werden im nächsten
   * Schritt gelesen. Die Messung braucht diesen ungebundenen Durchgang — an
   * einer Tabelle, die bereits eine Leiste trägt, misst man die Leiste.
   */
  const [vorgabe, setVorgabe] = useState<Spaltenbreiten | null>(null)

  // Der Haken haengt am Rahmen ODER an der Tabelle selbst: Die
  // Kapitalflussrechnung reicht ihre Tabelle durch mehrere Bildschirme und hat
  // ihren Rahmen nicht in derselben Hand.
  const [feld, rahmen] = useFeld<T>()

  // Der Bildwechsel holt die Breiten des neuen Bildes. Ohne das trüge das
  // Partnerbild die Breite, die für den Konzernaufriss gezogen wurde.
  useEffect(() => {
    setGezogen(gelesen(kennung))
    setVorgabe(null)
  }, [kennung])

  const merken = useCallback(
    (b: Spaltenbreiten | null) => {
      setGezogen(b)
      geschrieben(kennung, b)

      // Der Doppelklick auf den Griff gibt die gerechnete Breite zurück — und
      // „gerechnet" heißt seit R487 die gemessene Vorgabe, nicht der Zustand
      // vor der Messung. Also neu messen statt die alte Messung wiederholen:
      // Zwischen damals und jetzt kann ein Teilkonzern aufgeklappt sein.
      if (!b) {
        setVorgabe(null)
      }
    },
    [kennung],
  )

  useBreitenzug(feld, setGezogen, merken, vorspalten)
  useVorgabe(feld, gezogen !== null, vorgabe, setVorgabe)

  // **Gezogen sticht gemessen.** Wer einmal selbst gezogen hat, bekommt seine
  // Breite und nicht die Vorgabe — bis er sie per Doppelklick zurückgibt.
  return { breiten: gezogen ?? vorgabe, rahmen }
}

/** Die Tabelle unter einem Rahmen — oder der Rahmen selbst, wenn er die Tabelle ist. */
function tabelleIn(feld: HTMLElement | null): HTMLTableElement | null {
  if (!feld) {
    return null
  }

  return feld instanceof HTMLTableElement ? feld : feld.querySelector('table')
}

/**
 * Die natürlichen Breiten einer ungebundenen Tabelle — und daraus die Vorgabe
 * (R487).
 *
 * **Die breiteste Zahlenspalte gibt das Maß**, alle anderen ziehen nach. So
 * wird keine Zahl und keine Überschrift abgeschnitten, die vorher ganz
 * dastand — der Preis dafür ist Breite, und die holt sich die Vorgabe bei der
 * Positionsspalte: Sie ist die einzige Spalte, die ungezogen mehr nimmt, als
 * ihr Inhalt verlangt, und gibt den Zuwachs deshalb her, bis sie auf ihre
 * eigene Grundbreite fällt. Die Tabelle bleibt damit so breit wie vorher,
 * solange die 330 Pixel reichen — und rollt danach, wie sie auch vorher
 * gerollt wäre.
 *
 * Gelesen an den Kopfzellen und nicht an der ersten Zeile: Im Spiegel stünde
 * dort eine Blocküberschrift über sieben Spalten, und die Zahlenbreite wäre
 * das Siebenfache. Jede Spalte hat genau eine Kopfzelle ohne `colSpan` —
 * auch im zweizeiligen Kopf, denn die durchgehenden Spalten tragen dort
 * `rowSpan`.
 */
export function natuerlicheVorgabe(
  tab: HTMLTableElement,
  /**
   * Wie breit der Rahmen ist, in dem die Tabelle steht — der Deckel für die
   * Positionsspalte. Null heißt „nicht gemessen" (die Prüfumgebung kennt keine
   * Breiten); dann gilt kein Deckel.
   */
  rahmenbreite = 0,
): Spaltenbreiten | null {
  const kopf = tab.tHead
  if (!kopf) {
    return null
  }

  const breit = (e: Element) => e.getBoundingClientRect().width
  const zahlen = [...kopf.querySelectorAll('th.zahl')]
    .filter((th) => (th as HTMLTableCellElement).colSpan === 1)
    .map(breit)

  // Eine Tabelle ohne Zahlenspalten hat nichts gleichzumachen. Und eine, die
  // noch gar nicht gezeichnet ist, misst sich zu null — daraus eine Vorgabe zu
  // machen hieße, sie auf die Mindestbreiten zusammenzuschieben.
  if (zahlen.length === 0 || zahlen.some((b) => b === 0)) {
    return null
  }

  const wert = Math.round(Math.max(MINDEST_WERT, ...zahlen))
  const zuwachs = wert * zahlen.length - zahlen.reduce((s, b) => s + b, 0)

  const pos = kopf.querySelector('th.positionsspalte')
  const natuerlich = pos ? breit(pos) : VORGABE_POSITION
  let position = natuerlich - zuwachs

  // **Und nicht breiter als der Rahmen** (R487, präzisiert am 2026-09-14).
  // Ungebunden ist die Tabelle mindestens 1120 Pixel breit (`stil.css`) — das
  // Maß eines Zahlenwerks mit sieben Spalten, nicht das einer Tabelle mit
  // einer. Die Positionsspalte nähme alles, was die Zahlenspalten übrig
  // lassen, und schöbe die einzige Zahl des Periodenbilds aus dem Rahmen.
  // Der Rahmen gibt den Deckel: Die Positionsspalte bekommt, was neben den
  // anderen Spalten Platz hat, und nicht mehr. Die 330 bleiben die
  // Untergrenze — darunter rollt die Tabelle, wie sie es vorher tat.
  if (rahmenbreite > 0) {
    const andere = breit(tab) - natuerlich + zuwachs
    position = Math.min(position, rahmenbreite - andere)
  }

  return { position: Math.round(Math.max(VORGABE_POSITION, position)), wert }
}

/**
 * Misst die Vorgabe, sobald die Tabelle ungebunden dasteht — und wirft sie
 * weg, sobald sich die Tabelle ändert (R487).
 *
 * **Zwei Durchgänge, und das ist keine Umständlichkeit:** Der erste zeichnet
 * ohne Spaltenleiste und ist der einzige, in dem die natürlichen Breiten
 * überhaupt zu lesen sind; der zweite setzt sie ein. Weil die Messung in
 * einem `useLayoutEffect` steht, liegen beide vor demselben Bildaufbau — zu
 * sehen ist nur der zweite.
 *
 * **Was die Tabelle ändert, macht die Messung ungültig**: ein aufgeklappter
 * Teilkonzern, eine andere Sprache, ein anderer Bericht, ein anderes Fenster.
 * Deshalb hängt ein Beobachter am Rahmen. Die Spaltenleiste selbst ist davon
 * ausgenommen — sonst löste ihr eigenes Einsetzen die nächste Messung aus,
 * und die Tabelle käme nie zur Ruhe.
 */
function useVorgabe(
  feld: HTMLElement | null,
  ruht: boolean,
  vorgabe: Spaltenbreiten | null,
  setVorgabe: (b: Spaltenbreiten | null) => void,
) {
  useLayoutEffect(() => {
    if (ruht || vorgabe) {
      return
    }

    const tab = tabelleIn(feld)
    // Der Rahmen ist das Feld — oder, wo die Tabelle selbst am Haken hängt
    // (Kapitalflussrechnung), ihr Elternelement.
    const umgebung = feld instanceof HTMLTableElement ? feld.parentElement : feld
    const gemessen = tab && natuerlicheVorgabe(tab, umgebung?.clientWidth ?? 0)
    if (gemessen) {
      setVorgabe(gemessen)
    }
  })

  useEffect(() => {
    if (!feld) {
      return
    }

    const verwerfen = () => setVorgabe(null)

    /** Eine Änderung an der Spaltenleiste ist unsere eigene und zählt nicht. */
    const nurDieLeiste = (m: MutationRecord) =>
      [...m.addedNodes, ...m.removedNodes].every(
        (n) => n.nodeName === 'COLGROUP' || n.nodeName === 'COL',
      ) &&
      (m.addedNodes.length > 0 || m.removedNodes.length > 0)

    const beobachter = new MutationObserver((eintraege) => {
      if (!eintraege.every(nurDieLeiste)) {
        verwerfen()
      }
    })
    beobachter.observe(feld, { childList: true, subtree: true, characterData: true })

    // Nur die Breite des Rahmens zählt: Wird er höher, weil eine Zeile
    // aufklappt, hat das die Messung schon über den Mutationsbeobachter.
    //
    // **Wo es den Beobachter nicht gibt, gilt die Messung von vorhin** — wie
    // beim gesperrten Speicher weiter oben ist das kein Fehlerfall, sondern
    // eine Umgebung mit weniger Werkzeug (die Prüfumgebung ist eine davon).
    let breit = feld.clientWidth
    const masz =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            if (feld.clientWidth !== breit) {
              breit = feld.clientWidth
              verwerfen()
            }
          })
    masz?.observe(feld)

    return () => {
      beobachter.disconnect()
      masz?.disconnect()
    }
  }, [feld, setVorgabe])
}

/**
 * Die Spaltenleiste: Sie gibt der Tabelle ihre Breiten.
 *
 * **Sie steht immer da — gezogen oder gemessen** (R487, berichtigt am
 * 2026-09-13). Vorher stand sie erst nach dem ersten Zug, und ohne sie rechnete
 * der Browser jede Spalte aus ihrem eigenen Inhalt: Sieben Spalten des
 * Konsolidierungssachverhalts standen in sieben Breiten nebeneinander, und der
 * Blick sprang beim Vergleich zweier Zahlen über eine Stufe. Die Vorgabe ist
 * jetzt dieselbe Aussage wie der Zug: **eine Breite für alle Zahlenspalten.**
 *
 * Für einen Augenblick fehlt sie doch — im ersten Durchgang, in dem gemessen
 * wird (siehe `useVorgabe`). Der wird nie gezeichnet.
 */
export function Spaltenleiste({
  breiten,
  spalten,
  vorspalten = [],
}: {
  breiten: Spaltenbreiten | null
  /** Wie viele Spalten die Tabelle hat, alle eingeschlossen. */
  spalten: number
  /**
   * Schmale Spalten VOR der Positionsspalte, je eine Breite — die
   * Nummernspalte der Kapitalflussrechnung ist die einzige. Sie trägt vier
   * Zeichen und wäre nichts, was man zieht.
   *
   * **Sie braucht ihre Breite ausdrücklich**, sie kann sie nicht behalten: In
   * einer fest aufgeteilten Tabelle bekommt eine Spalte ohne Breite, was
   * übrig bleibt — und übrig bleibt nichts, weil die Tabelle genau so breit
   * ist wie ihre Spalten. Ohne diese Angabe fiel die Nummernspalte auf null
   * zusammen (nachgemessen am 2026-09-12).
   */
  vorspalten?: string[]
}) {
  if (!breiten || spalten < 2 + vorspalten.length) {
    return null
  }

  return (
    <colgroup>
      {vorspalten.map((breite, i) => (
        <col key={`vor${i}`} style={{ width: breite }} />
      ))}
      <col style={{ width: `${breiten.position}px` }} />
      {Array.from({ length: spalten - 1 - vorspalten.length }, (_, i) => (
        <col key={i} style={{ width: `${breiten.wert}px` }} />
      ))}
    </colgroup>
  )
}

/**
 * **Was ein Zug an der Spaltenkante ändert** — eine der beiden Gruppen oder
 * die Spalte an dieser Stelle.
 *
 * Die Zahl ist der Spaltenindex und gehört der zweiten Betriebsart (siehe
 * `useSpaltenmasse`): Eine Arbeitstabelle hat keine Gruppe „alle
 * Zahlenspalten", weil ihre Spalten nichts gemeinsam haben — Kürzel, Name,
 * Währung, Datum stehen nebeneinander und wollen jede ihre eigene Breite.
 */
type Zugziel = 'position' | 'wert' | number

/**
 * **Was die beiden Betriebsarten verschieden machen** — und sonst nichts.
 *
 * Der Griff, die Trefferfläche um die Linie, das Markieren beim Überfahren,
 * das Schreiben an die Leiste während des Zugs und das Merken beim Loslassen
 * sind in beiden Arten dasselbe und stehen deshalb genau einmal da
 * (`useZug`). Verschieden ist nur, **was** ein Griff ändert und **wie** das
 * gemessen und an die Leiste geschrieben wird.
 */
type Zugplan<S> = {
  /** Was die Kante dieser Kopfzelle ändert — `null` heißt: kein Griff. */
  zielVon: (th: HTMLTableCellElement, tab: HTMLTableElement) => Zugziel | null
  /** Die Breiten, wie der Browser sie gerade gerechnet hat. */
  gemessen: (tab: HTMLTableElement) => S
  /** Derselbe Stand mit einer geänderten Breite. */
  mitBreite: (stand: S, ziel: Zugziel, breite: number) => S
  /** Was während des Zugs an die Spaltenleiste geschrieben wird. */
  anDieLeiste: (stand: S, leiste: Element) => void
  /** Unter diese Breite geht es nicht. */
  mindest: (ziel: Zugziel) => number
}

/**
 * Der Zug an der Spaltenkante.
 *
 * **Gemeldet wird beim Loslassen, gezeigt schon währenddessen** — dieselbe
 * Trennung wie beim Zeitschieber (R334). Ein Bericht je Mausbewegung ließe
 * eine Tabelle mit vierhundert Zeilen und zwanzig Spalten unter der Maus
 * ruckeln; deshalb schreibt der Zug die Breite unmittelbar an die
 * Spaltenleiste im DOM und erst das Loslassen in den Zustand.
 *
 * **Zwei Betriebsarten, eine Mechanik** (Auftrag vom 2026-09-29). Was ein
 * Griff ändert, sagt der Plan; alles übrige — die Trefferfläche um die Linie,
 * das Markieren beim Überfahren, das Schreiben an die Leiste, das Merken beim
 * Loslassen — steht hier genau einmal.
 */
function useZug<S>(
  feld: HTMLElement | null,
  plan: Zugplan<S>,
  setBreiten: (b: S) => void,
  merken: (b: S | null) => void,
) {
  useEffect(() => {
    if (!feld) {
      return
    }

    // Die Tabelle wird bei jedem Griff neu gesucht und nicht hier festgehalten:
    // Ein Bildwechsel tauscht sie aus, waehrend der Rahmen stehen bleibt.
    const tabelle = () =>
      feld instanceof HTMLTableElement ? feld : feld.querySelector('table')

    let marke: HTMLTableCellElement | null = null
    let zug: {
      ziel: Zugziel
      ab: number
      start: number
      stand: S
    } | null = null

    /**
     * Ob eine Kopfzelle eine Kante zum Ziehen hat.
     *
     * Eine Zelle über mehreren Spalten (das Band eines Teilkonzerns, die
     * Blocküberschrift des Spiegels) hat keine eigene: Welche der Spalten
     * darunter sollte sie breiter machen? Was darüber hinaus keinen Griff
     * trägt, sagt der Plan.
     */
    function ziehbar(th: Element | null | undefined): th is HTMLTableCellElement {
      const tab = tabelle()
      return (
        th instanceof HTMLTableCellElement &&
        tab !== null &&
        th.closest('thead') !== null &&
        th.colSpan === 1 &&
        plan.zielVon(th, tab) !== null
      )
    }

    /**
     * Die Kopfzelle, an deren rechter Kante der Zeiger steht — sonst nichts.
     *
     * **Die Fläche liegt beidseits der Linie** (R448) und reicht damit über
     * die Zellgrenze: Fünf Pixel hinter der Kante steht der Zeiger schon in
     * der nächsten Überschrift, gemeint ist aber die Kante davor. Wer nur die
     * Zelle unter dem Zeiger fragte, griffe dort ins Leere — die Kante gehört
     * der Zelle **links** von ihr.
     */
    function amGriff(e: PointerEvent | MouseEvent): HTMLTableCellElement | null {
      const ziel = e.target as HTMLElement | null
      const th = ziel?.closest?.('th')
      if (!(th instanceof HTMLTableCellElement) || !th.closest('thead')) {
        return null
      }

      const kasten = th.getBoundingClientRect()

      if (ziehbar(th) && e.clientX >= kasten.right - GRIFF) {
        return th
      }

      // Links im Feld: gemeint ist die Kante der Spalte davor.
      if (e.clientX <= kasten.left + GRIFF) {
        const davor = th.previousElementSibling
        return ziehbar(davor) ? davor : null
      }

      return null
    }

    /** Während des Zugs geht die Breite direkt an die Spaltenleiste. */
    function anDieLeiste(stand: S): void {
      const leiste = tabelle()?.querySelector('colgroup')
      if (leiste) {
        plan.anDieLeiste(stand, leiste)
      }
    }

    function markieren(th: HTMLTableCellElement | null): void {
      if (marke === th) {
        return
      }

      marke?.classList.remove('amgriff')
      th?.classList.add('amgriff')
      marke = th
    }

    function beiSchweben(e: PointerEvent): void {
      if (!zug) {
        markieren(amGriff(e))
      }
    }

    function beiDruck(e: PointerEvent): void {
      const th = amGriff(e)
      const tab = tabelle()
      if (!th || !tab || e.button !== 0) {
        return
      }

      // Sonst fängt der Browser an, die Überschrift zu markieren, und die
      // Zeile darunter klappt auf, weil sie einen Klick gesehen hat.
      e.preventDefault()
      e.stopPropagation()

      const ziel = plan.zielVon(th, tab) ?? 'wert'
      const start = Math.round(th.getBoundingClientRect().width)

      // **Die gezogene Spalte springt nicht.** Sie behält ihre gemessene
      // Breite und wächst von dort; die anderen ihrer Gruppe rücken auf sie
      // zu. Andersherum — alle auf die breiteste — fühlte sich an, als hätte
      // der Griff etwas anderes angefasst als das, was unter ihm lag.
      const stand = plan.mitBreite(plan.gemessen(tab), ziel, start)

      zug = { ziel, ab: e.clientX, start, stand }
      setBreiten(stand)
      anDieLeiste(stand)

      document.body.classList.add('beimziehen')
      window.addEventListener('pointermove', beiZug)
      window.addEventListener('pointerup', beiLoslassen, { once: true })
    }

    function beiZug(e: PointerEvent): void {
      if (!zug) {
        return
      }

      const neu = Math.max(plan.mindest(zug.ziel), zug.start + (e.clientX - zug.ab))
      zug.stand = plan.mitBreite(zug.stand, zug.ziel, Math.round(neu))
      anDieLeiste(zug.stand)
    }

    function beiLoslassen(): void {
      window.removeEventListener('pointermove', beiZug)
      document.body.classList.remove('beimziehen')

      if (zug) {
        merken(zug.stand)
        zug = null
      }
    }

    /** Doppelklick auf den Griff gibt die gerechnete Breite zurück. */
    function beiDoppelklick(e: MouseEvent): void {
      if (!amGriff(e)) {
        return
      }

      e.preventDefault()
      e.stopPropagation()
      merken(null)
    }

    feld.addEventListener('pointermove', beiSchweben)
    feld.addEventListener('pointerdown', beiDruck)
    feld.addEventListener('dblclick', beiDoppelklick)

    return () => {
      feld.removeEventListener('pointermove', beiSchweben)
      feld.removeEventListener('pointerdown', beiDruck)
      feld.removeEventListener('dblclick', beiDoppelklick)
      window.removeEventListener('pointermove', beiZug)
      document.body.classList.remove('beimziehen')
      markieren(null)
    }
  }, [feld, plan, setBreiten, merken])
}

/**
 * Der Plan der **zwei Gruppen** (R416): die Positionsspalte für sich, alle
 * Zahlenspalten gemeinsam.
 */
function gruppenplan(vorspalten: number): Zugplan<Spaltenbreiten> {
  /**
   * Zu welcher Gruppe eine Kopfzelle gehört — und ob sie überhaupt einen
   * Griff trägt.
   *
   * **Die Klasse sagt es, nicht die Lage.** Die Positionsspalte ist nicht
   * überall die erste: In der Kapitalflussrechnung steht die Nummer des
   * DRS-21-Standards davor, und in der zweiten Kopfzeile eines aufgeklappten
   * Teilkonzerns fängt die erste Spalte mitten in der Tabelle an. Wer hier
   * zählt statt zu lesen, zieht die falsche Spalte.
   *
   * Eine Zelle ohne beide Klassen — die Nummernspalte, die leere Ecke über
   * der Position im Spiegel — bekommt keinen Griff.
   */
  function gruppeVon(th: HTMLTableCellElement): 'position' | 'wert' | null {
    if (th.classList.contains('positionsspalte')) {
      return 'position'
    }

    return th.classList.contains('zahl') ? 'wert' : null
  }

  return {
    zielVon: gruppeVon,

    /**
     * Die Breiten, wie der Browser sie gerade gerechnet hat.
     *
     * Gelesen an den Kopfzellen und nicht an der ersten Zeile: Im Spiegel
     * stünde dort eine Blocküberschrift über sieben Spalten, und die
     * Zahlenbreite wäre das Siebenfache.
     */
    gemessen(tab) {
      const kopf = tab.tHead
      const breit = (e: Element) => e.getBoundingClientRect().width

      const position = kopf?.querySelector('th.positionsspalte')
      const zahlen = [...(kopf?.querySelectorAll('th.zahl') ?? [])].filter(
        (th) => (th as HTMLTableCellElement).colSpan === 1,
      )

      return {
        position: Math.round(position ? breit(position) : 330),
        // Die breiteste: Beim Zusammenlegen soll keine Zahl abgeschnitten
        // werden, die vorher ganz dastand.
        wert: Math.round(Math.max(MINDEST_WERT, ...zahlen.map(breit))),
      }
    },

    mitBreite: (stand, ziel, breite) => ({ ...stand, [ziel as 'position' | 'wert']: breite }),

    anDieLeiste(stand, leiste) {
      // **Die Vorspalten bleiben, wo sie sind.** Die Positionsspalte ist nicht
      // überall die erste: In der Kapitalflussrechnung steht die Nummer des
      // DRS-21-Standards davor. Wer hier ab null zählt, gibt ihr während des
      // Zugs die Breite der Positionsspalte und dieser die einer Zahlenspalte.
      for (const [i, col] of [...leiste.children].entries()) {
        if (i < vorspalten) {
          continue
        }

        ;(col as HTMLElement).style.width = `${i === vorspalten ? stand.position : stand.wert}px`
      }
    },

    mindest: (ziel) => (ziel === 'position' ? MINDEST_POSITION : MINDEST_WERT),
  }
}

function useBreitenzug(
  feld: HTMLElement | null,
  setBreiten: (b: Spaltenbreiten) => void,
  merken: (b: Spaltenbreiten | null) => void,
  vorspalten: number,
) {
  const plan = useMemo(() => gruppenplan(vorspalten), [vorspalten])
  useZug(feld, plan, setBreiten, merken)
}

/* ------------------------------------------------------------------ */
/* Die zweite Betriebsart: jede Spalte für sich                         */
/* ------------------------------------------------------------------ */

/**
 * **Die Breite jeder einzelnen Spalte**, von links nach rechts (7.3 Abschnitt
 * 10.6a, R833 — Auftrag vom 2026-09-29: *„Bitte lass die Größe des Fensters und auch die Breite der
 * Spalten vom Anwender veränderbar sein"* — und zwar in jeder Tabelle der
 * Anwendung, nicht nur in der IC-Abstimmung).
 *
 * **Warum nicht die zwei Gruppen aus R416.** Die fünf Berichtstabellen haben
 * eine Textspalte und zwanzig Zahlenspalten derselben Größenordnung in
 * derselben Währung — dort ist „eine Breite für alle Zahlenspalten" die
 * richtige Einteilung und spart neunzehn Züge. Eine Arbeitstabelle hat
 * Kürzel, Name, Währung, Datum und Zustand nebeneinander; die haben nichts
 * gemeinsam, und eine gemeinsame Breite wäre für jede von ihnen die falsche.
 *
 * **Der Preis** ist benannt: Wer in einer Tabelle mit zwölf Spalten drei
 * davon gerade rücken will, zieht dreimal. Bei Spalten, die verschiedene
 * Dinge tragen, ist das aber keine Wiederholung, sondern jedesmal eine andere
 * Entscheidung.
 */
export type Spaltenmasse = readonly number[]

/** Die Stelle, an der eine Kopfzelle in der Tabelle beginnt. */
function stelleVon(th: HTMLTableCellElement): number {
  let stelle = 0
  for (const zelle of [...(th.parentElement as HTMLTableRowElement).cells]) {
    if (zelle === th) {
      return stelle
    }
    stelle += zelle.colSpan
  }
  return stelle
}

/**
 * Der Plan **je Spalte**.
 *
 * Gegriffen wird nur in der **ersten** Kopfzeile: Wo ein Kopf zwei Zeilen
 * hat, steht in der zweiten eine Unterteilung, und deren Kanten liegen quer
 * zu den Spalten der ersten. Der Plan bleibt damit auch an einem gestaffelten
 * Kopf bei dem, was er verspricht — er greift dort nur weniger weit.
 */
const SPALTENPLAN: Zugplan<Spaltenmasse> = {
  zielVon(th, tab) {
    const erste = tab.tHead?.rows[0]
    return th.parentElement === erste ? stelleVon(th) : null
  },

  /**
   * Alle Spalten so breit, wie sie gerade dastehen.
   *
   * Eine Zelle über mehreren Spalten gibt ihre Breite zu gleichen Teilen an
   * sie ab: Sie selbst hat keinen Griff, aber die Leiste braucht **jede**
   * Spalte — eine Spalte ohne Breite bekäme in einer fest aufgeteilten
   * Tabelle, was übrig ist, und übrig ist nichts (nachgemessen am 2026-09-12
   * an der Belegliste, siehe `stil.css`).
   */
  gemessen(tab) {
    const erste = tab.tHead?.rows[0]
    if (!erste) {
      return []
    }

    const masse: number[] = []
    for (const zelle of [...erste.cells]) {
      const breite = zelle.getBoundingClientRect().width / zelle.colSpan
      for (let i = 0; i < zelle.colSpan; i++) {
        masse.push(Math.round(Math.max(MINDEST_WERT, breite)))
      }
    }

    return masse
  },

  mitBreite: (stand, ziel, breite) => stand.map((alt, i) => (i === ziel ? breite : alt)),

  anDieLeiste(stand, leiste) {
    for (const [i, col] of [...leiste.children].entries()) {
      if (stand[i] !== undefined) {
        ;(col as HTMLElement).style.width = `${stand[i]}px`
      }
    }
  },

  mindest: () => MINDEST_WERT,
}

/** Die Ablage der Maße — dieselbe Stelle, ein anderer Schnitt (R417). */
function masseGelesen(kennung: string): Spaltenmasse | null {
  try {
    const roh = window.localStorage.getItem(ABLAGE + kennung)
    if (!roh) {
      return null
    }

    const m = JSON.parse(roh) as unknown
    return Array.isArray(m) && m.length > 0 && m.every((b) => typeof b === 'number')
      ? (m as Spaltenmasse)
      : null
  } catch {
    // Ein Browser ohne Speicher (privates Fenster, gesperrte Seitendaten) ist
    // kein Fehlerfall: Dann rechnet der Browser die Breiten, wie vorher.
    return null
  }
}

function masseGeschrieben(kennung: string, masse: Spaltenmasse | null): void {
  try {
    if (masse) {
      window.localStorage.setItem(ABLAGE + kennung, JSON.stringify(masse))
    } else {
      window.localStorage.removeItem(ABLAGE + kennung)
    }
  } catch {
    /* siehe oben */
  }
}

/**
 * Die Breiten einer Arbeitstabelle — gezogen und gemerkt, **eine je Spalte**.
 *
 * **Ohne Zug steht keine Leiste da**, und damit rechnet der Browser die
 * Breiten wie bisher. Das ist der Unterschied zu `useSpaltenbreiten`, das
 * eine gemessene Vorgabe einsetzt, um alle Zahlenspalten gleich breit zu
 * machen: Hier gibt es nichts gleichzumachen, und eine Tabelle, die heute
 * richtig steht, soll ihr Aussehen nicht dadurch ändern, dass man an ihr
 * ziehen **kann**. **R654 bleibt damit unberührt** — die festen Breiten einer
 * Arbeitstabelle stehen weiter fest, bis jemand von Hand zieht.
 *
 * `kennung` benennt das Bild, nicht die Tabelle.
 */
export function useSpaltenmasse<T extends HTMLElement = HTMLDivElement>(kennung: string) {
  const [gezogen, setGezogen] = useState<Spaltenmasse | null>(() => masseGelesen(kennung))
  const [feld, rahmen] = useFeld<T>()

  // Der Bildwechsel holt die Maße des neuen Bildes.
  useEffect(() => {
    setGezogen(masseGelesen(kennung))
  }, [kennung])

  const merken = useCallback(
    (m: Spaltenmasse | null) => {
      setGezogen(m)
      masseGeschrieben(kennung, m)
    },
    [kennung],
  )

  /*
    **Maße, die nicht mehr passen, gelten nicht** (Auftrag vom 2026-09-29).

    Ein Bild mit zwölf Perioden und eines mit dreien sind nicht dieselbe
    Tabelle. Eine Leiste mit zwölf Einträgen an einer Tabelle mit vieren gäbe
    den überzähligen Spalten Breiten, die es nicht gibt — und der Rest der
    Tabelle bekäme, was übrig ist, also nichts (nachgemessen am 2026-09-12 an
    der Belegliste, siehe `stil.css`).

    Geprüft wird hier und nicht an den siebenundzwanzig Aufrufstellen: Die
    Spaltenzahl steht in der Tabelle selbst, und jede Aufrufstelle müsste sie
    sonst ein zweites Mal ausrechnen. Verworfen wird nur der Zustand, nicht
    die Ablage — wer zwischendurch ein anderes Bild ansieht, findet seine
    Breiten danach wieder.
  */
  useLayoutEffect(() => {
    const tab = tabelleIn(feld)
    const erste = tab?.tHead?.rows[0]
    if (!gezogen || !erste) {
      return
    }

    const spalten = [...erste.cells].reduce((summe, zelle) => summe + zelle.colSpan, 0)
    if (spalten > 0 && spalten !== gezogen.length) {
      setGezogen(null)
    }
  }, [feld, gezogen])

  useZug(feld, SPALTENPLAN, setGezogen, merken)

  return { masse: gezogen, rahmen }
}

/**
 * Die Spaltenleiste der zweiten Betriebsart.
 *
 * **Sie steht erst nach dem ersten Zug da** (siehe `useSpaltenmasse`): Ohne
 * Zug rechnet der Browser die Breiten, wie er es immer getan hat, und eine
 * Tabelle ändert ihr Aussehen nicht dadurch, dass man an ihr ziehen **kann**.
 *
 * **Sie braucht keine Spaltenzahl.** Ob die Maße noch zur Tabelle passen,
 * prüft der Haken an der Tabelle selbst — die Aufrufstelle müsste sie sonst
 * ein zweites Mal ausrechnen, und das an siebenundzwanzig Stellen.
 */
export function Spaltenmassleiste({ masse }: { masse: Spaltenmasse | null }) {
  if (!masse) {
    return null
  }

  return (
    <colgroup>
      {masse.map((breite, i) => (
        <col key={i} style={{ width: `${breite}px` }} />
      ))}
    </colgroup>
  )
}
